/**
 * Visibility rule for `<TerrainViewSwitch />`.
 *
 * Elevation data reaches the catalog under two different kinds: the terrain
 * slot (`kind: 'terrain'`) and, for converter COG output, `kind: 'raster'`
 * with DEM/DTM hints on the label or style. Gating on `kind === 'terrain'`
 * alone hid the switch for exactly the case operators asked for it — a DTM COG
 * opened through the Raster ToolCard.
 *
 * MapLibre, deck.gl, and OpenLayers Canvas all count as the 2D side so the
 * control stays visible after a WebGL fallback. Giro3D is the 3D side.
 */

import type { EngineId } from '@geosoft/viewer-sdk';
import { isDemLikeLayer } from '@/utils/titilerDemColorize';

type GateLayer = Parameters<typeof isDemLikeLayer>[0] & { kind: string };

/** True when the scene holds terrain-slot or DEM-like raster elevation data. */
export function sceneHasElevationLayer(
  layers: ReadonlyArray<GateLayer>,
): boolean {
  return layers.some(
    (layer) => layer.kind === 'terrain' || isDemLikeLayer(layer),
  );
}

/** Engines that render as the "2D map" chip. */
export function isTerrainView2DEngine(engine: EngineId): boolean {
  return engine === 'maplibre' || engine === 'deckgl' || engine === 'openlayers';
}

/**
 * Engines that can take part in the 2D ↔ 3D toggle. Cesium / Potree / Three
 * stay on their own chrome; the Raster card still exposes the switch when
 * an elevation layer is selected so operators can jump to MapLibre/Giro3D.
 */
export function engineParticipatesInTerrainView(engine: EngineId): boolean {
  return isTerrainView2DEngine(engine) || engine === 'giro3d';
}

export function shouldShowTerrainViewSwitch(
  engine: EngineId,
  layers: ReadonlyArray<GateLayer>,
): boolean {
  return engineParticipatesInTerrainView(engine) && sceneHasElevationLayer(layers);
}
